import { useQuery, useLazyQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import type {
  Student,
  Setting,
  StudentPaginator,
  DashboardStats,
} from '@/types/composition-functions';
import { PAGINATOR_FRAGMENT, STUDENT_BILLS_FRAGMENT, STUDENT_FRAGMENT } from '../Fragments';

const QUERY_STUDENT_BY_ID = ({
  id,
  autoFetch = true,
  include_tertiary = false,
}: {
  id: number
  autoFetch?: boolean
  include_tertiary?: boolean
}) => {
  const queryResult = useLazyQuery<{ student: Student }, { id: number, include_tertiary: boolean }>(
    gql`
      query QueryStudentById($id: ID!, $include_tertiary: Boolean = false) {
        student(id: $id) {
          id
        matric_no
        linking_code
        first_name
        surname
        other_name
        dob
        email
        gender
        phone
        home_address
        nationality
        state
        city
        currentClassroomSection {
          id
          name
          school {
           id
           name
           school_type
           address
           website
           email
           mobile
           classrooms {
            id
            name
            }
              academicCalendars {
                id
                is_active
                session {
                  id
                  name
                }
                termStructure {
                  id
                  name
                }
              }
          }
          classroom {
            id
            name
            classroomSections {
              id
              name
             }
            sections {
              id
               name
              }
          }
          section {
            id
            name
          }
        }
        school{
          id,
          name
          academicCalendars {
            id
            is_active
            session {
              id
              name
            }
            termStructure {
              id
              name
            }
          }
          billItems {
            id
            name
          }
        }
        studentBills {
            id
            billable_type
            billable_id
            amount
            due_date
            status
            school{
              id,
              name
              academicCalendars {
                id
            is_active
                session {
                  id
                  name
                }
                termStructure {
                  id
                  name
                }
              }
            }
            session {
              id
              name
            }
            academicCalendar {
              session {
                  id,
                  name
              }
              termStructure {
                id
                name
              }
            }
            billDetails {
              id
              amount
              billItemTemplate {
                id
                name
                description
                default_amount
                description
              }
            }

            billable {
              ... on Classroom {
                id
                name
                abbreviation
              }
               ... on ClassroomSection {
                  id
                  classroom_id
                  section_id
                  classroom {
                    id
                    name
                    abbreviation
                  }
                  section {
                    id
                    name
                  }
                }
              ... on FormSubmission {
                id
                tracking_code
                applicant_name
              }
              ... on TranscriptRequest {
                id
                request_applicant_name: applicant_name
                request_tracking_code: tracking_code
              }
            }
          }

          academicCalendars {
            id
            is_active
            session { id name }
            termStructure { id name }
          }

          studentAcademicCalendar {
           id
           programme @include(if: $include_tertiary) {
             delivery_mode
             min_units_per_registration
             max_units_per_registration
             progression_type
             id
             program_name
              prog_code
              degree
              type
           }
           max_units_per_registration
           entry_level
           min_graduation_units
           min_units_per_registration
           initial_late_days
           initial_present_days
           initial_absent_days

        }

        studentAcademicCalendars @include(if: $include_tertiary) {
          id
          academic_calendar_id
          status
          programme_id
          programme {
            id
            program_name
            degree
          }
          classroomSection {
            id
            name
            classroom { id name }
          }
          academicCalendar {
            id
            session { id name }
            termStructure { id name }
          }
          max_units_per_registration
           entry_level
           min_graduation_units
          min_units_per_registration
          created_at
        }

        lastAcademicRecord {
          id
          status
          academicCalendar {
            id
            session {
              id
              name
            }
            termStructure {
              id
              name
            }
          }
          classroomSection {
            id
            classroom {
              id
              name
            }
            section {
              id
              name
            }
          }
          programme {
            id
            program_name
          }
        }

        openProgrammeModules @include(if: $include_tertiary) { id name registration_fee sequence academic_calendar_id min_units_per_registration max_units_per_registration started_at ended_at is_open }
        studentActiveCurriculum @include(if: $include_tertiary) {
          programme_module_id
          moduleOfferings { id }
          id
          course_id
          programme_id
          status
          semester
          course_registration_level
          school_id
          is_active

          course {
            id
            course_code
            course_title
            unit
            course_instance_id
            course_instance_year
            is_active


          }

        }

        studentCourseRegistration @include(if: $include_tertiary) {
          id
          student_id
          academic_session
          semester
          registration_level
          programme_id
          programme {
            id
            program_name
            prog_code
            degree
            type
          }

          coursesRegistered {
          programme_module_id
          module_name
          module_sequence
           id
           course_id
           course_status
            course{
              id
              course_code
              course_title
              unit
            }
            scores {
              id
              score

            }

          }

        }

        studentRegistrationForCurrentSemester @include(if: $include_tertiary) {
          id
          student_id
          academic_session
          semester
          registration_level
          programme_id
          programme {
            id
            program_name
          }

          coursesRegistered {
          programme_module_id
          module_name
          module_sequence
           id
           course_id
           course_status
            course{
              id
              course_code
              course_title
              unit
            }
            scores {
              id
              score

            }

          }

        }



        }
      }
    `,
    { id, include_tertiary },
  );
  if (autoFetch) (queryResult as unknown as { load?: () => void }).load?.();
  return queryResult;
};

const QUERY_ACTIVE_SETTING = () =>
  useLazyQuery<{ activeSetting: Setting }, Record<string, never>>(gql`
    query {
      activeSetting {
        sessionId
        termId
        id
        sessionName
        termName
        totalSchoolDay
      }
    }
  `);

const QUERY_STUDENTS = ({ page, rowsPerPage, include_tertiary = false }: { page: number; rowsPerPage: number; include_tertiary?: boolean }) =>
  useLazyQuery<
    { students: StudentPaginator },
    { page: number; first: number; include_tertiary: boolean }
  >(
    gql`
      query GetStudents($page: Int, $first: Int, $include_tertiary: Boolean = false) {
        students(page: $page, first: $first) {
          paginatorInfo {
            currentPage
            total
            perPage
          }
          data {

             id
        matric_no
        first_name
        surname
        other_name
        dob
        email
        gender
        phone
        home_address
        nationality
        state
        city
        currentClassroomSection {
          id
          name
          school {
           id
           name
           school_type
           address
           website
           email
           mobile
           classrooms {
            id
            name
            }
              academicCalendars {
                id
            is_active
                session {
                  id
                  name
                }
                termStructure {
                  id
                  name
                }
              }
          }
          classroom {
            id
            name
          }
          section {
            id
            name
          }
        }
        school{
          id,
          name
          academicCalendars {
            id
            is_active
            session {
              id
              name
            }
            termStructure {
              id
              name
            }
          }
          billItems {
            id
            name
          }
        }
        studentBills {
            id
            billable_type
            billable_id
            amount
            due_date
            status
            school{
              id,
              name
              academicCalendars {
                id
            is_active
                session {
                  id
                  name
                }
                termStructure {
                  id
                  name
                }
              }
            }
            session {
              id
              name
            }
            academicCalendar {
              session {
                  id,
                  name
              }
              termStructure {
                id
                name
              }
            }
            billDetails {
              id
              amount
              billItemTemplate {
                id
                name
                description
                default_amount
                description
              }
            }

            billable {
              ... on Classroom {
                id
                name
                abbreviation
              }
               ... on ClassroomSection {
                  id
                  classroom_id
                  section_id
                  classroom {
                    id
                    name
                    abbreviation
                  }
                  section {
                    id
                    name
                  }
                }
              ... on FormSubmission {
                id
                tracking_code
                applicant_name
              }
              ... on TranscriptRequest {
                id
                request_applicant_name: applicant_name
                request_tracking_code: tracking_code
              }
            }
         }
         studentAcademicCalendar {
            id
            programme @include(if: $include_tertiary) {
             delivery_mode
             min_units_per_registration
             max_units_per_registration
             progression_type
                id
                program_name
                department {
                    id
                    faculty {
                        id
                        school_id
                    }
                }
            }
            academicCalendar {
                id
                termStructure {
                    id
                    name
                }
                session {
                    id
                    name
                }
            }
         }

         openProgrammeModules @include(if: $include_tertiary) { id name registration_fee sequence academic_calendar_id min_units_per_registration max_units_per_registration started_at ended_at is_open }
        studentActiveCurriculum @include(if: $include_tertiary) {
          programme_module_id
          moduleOfferings { id }
          id
          course_id
          programme_id
          status
          semester
          course_registration_level
          school_id
          is_active

          course {
            id
            course_code
            course_title
            unit
            course_instance_id
            course_instance_year
            is_active
          }

        }
        studentRegistrationForCurrentSemester @include(if: $include_tertiary) {
          id
          student_id
          academic_session
          semester
          registration_level
          programme_id
          programme {
            id
            program_name
          }

          coursesRegistered {
          programme_module_id
          module_name
          module_sequence
           id
           course_id
           course_status
            course{
              id
              course_code
              course_title
              unit
            }
            scores {
              id
              score
            }
          }
        }

      }
          }
        }
      }
    `,
    { page, first: rowsPerPage, include_tertiary },
  );

const QUERY_STUDENT_SESSION = ({
  sessionId,
  termId,
  studentId,
}: { sessionId?: number; termId?: number; studentId?: number } = {}) =>
  useLazyQuery<{ studentSession: Record<string, unknown> }, Record<string, never>>( // StudentSession type not yet generated
    gql`
      query GetStudentSession($sessionId: ID!, $termId: ID!, $studentId: ID!) {
        studentSession(session_id: $sessionId, term_id: $termId, student_id: $studentId) {
          studentId
          session {
            session
          }
          term {
            name
          }
          studentSubjects {
            testOne
            testTwo
            testThree
            exam
            cumulative
            lastTermCumulative
            subject {
              name
            }
          }
        }
      }
    `,
    { sessionId, termId, studentId },
  );

// const QUERY_SETTING = ({ sessionId, termId }: { sessionId: number; termId: number }) => {
//   return useLazyQuery<{ getSetting: Setting }, { sessionId: number; termId: number }>(
//      gql`
//     query GetSetting($sessionId: ID!, $termId: ID!) {
//       getSetting(session_id: $sessionId, term_id: $termId) {
//         sessionName
//         termId
//         isActive
//         totalSchoolDay
//       }
//     }
//   `,
//     { sessionId, termId }
//   )
// }
const QUERY_SETTING = gql`
  query GetSetting($sessionId: ID!, $termId: ID!) {
    getSetting(session_id: $sessionId, term_id: $termId) {
      sessionName
      termId
      isActive
      totalSchoolDay
    }
  }
`;

const QUERY_STUDENT_SESSIONS = ({ studentId }: { studentId: number }) =>
  useLazyQuery<
    { student: { id: string; academicCalendars?: Array<Record<string, unknown>> } },
    { studentId: number }
  >( // Student sessions query
    gql`
      query GetStudentSessions($studentId: ID!) {
        student(id: $studentId) {
          id
          academicCalendars {
            id
            is_active
            session {
              id
              name
            }
            termStructure {
              id
              name
            }
             # Remarks are not currently available on AcademicCalendar or Student
          }
        }
      }
    `,
    { studentId },
  );

const GET_CURRENT_USER = gql`
query getCurrentUser($include_tertiary: Boolean = false) {
  me {
    __typename
    ... on User {
      id
      name
      email
      tour_intro_dismissed_at
      permissions {
        id
        action
        resource
        key
      }
      permissionGroup {
        id
        name
      }
      schools {
        id
        name
        slug
        frontend_url
        school_type
        student_portal_mode
        logo
        is_parent
        parent_features_enabled
      }
      guardianProfile {
        id
      }
      availablePortals
      staffProfile {
        id
        subjects {
          subject {
            id
            name
          }
          classroomSections {
            id
            name
            classroom {
              id
              name
            }
          }
        }
        lecturer_courses_in_semester @include(if: $include_tertiary) {
          id
          course {
            id
            course_code
            course_title
            unit
            course_instance_id
            course_instance_year
          }
          is_approved
        }
      }
    }
    ... on Student {
      id
      matric_no
      first_name
      surname
      other_name
      dob
      email
      is_active
      tour_intro_dismissed_at
      gender
      phone
      home_address
      nationality
      state
      city
      photo
      academicCalendars {
        id
        is_active
        started_at
        next_calender_starts_at
        session {
          id
          name
        }
        termStructure {
          id
          name
        }
      }
      currentClassroomSection {
        id
        name
        school {
          id
          name
          address
          website
          email
          school_type
          mobile
          logo
          settings
          classrooms {
            id
            name
          }
          academicCalendars {
            id
            is_active
            session {
              id
              name
            }
            termStructure {
              id
              name
            }
          }
        }
        classroom {
          id
          name
        }
        section {
          id
          name
        }
      }
      school {
        id
        name
        ticket_auto_response
        school_type
        student_portal_mode
        address
        website
        email
        logo
        settings
        academicCalendars {
          id
            is_active
          session {
            id
            name
          }
          termStructure {
            id
            name
          }
        }
        billItems {
          id
          name
        }
      }
      studentAcademicCalendar {
        id
        programme {
          delivery_mode
             min_units_per_registration
             max_units_per_registration
          progression_type
          id
          program_name
          prog_code
          degree
          type
        }
        max_units_per_registration
           entry_level
           min_graduation_units
        min_units_per_registration
        initial_late_days
        initial_present_days
        initial_absent_days
      }
      studentBills {
        id
        billable_type
        billable_id
        amount
        net_amount
        amount_paid
        balance
        due_date
        status
        paymentStatusDetails {
          payment_status
          total_amount
          amount_paid
          balance
        }
        school {
          id
          name
          academicCalendars {
            id
            is_active
            session {
              id
              name
            }
            termStructure {
              id
              name
            }
          }
        }
        session {
          id
          name
        }
        academicCalendar {
          session {
            id
            name
          }
          termStructure {
            id
            name
          }
        }
        billDetails {
          id
          amount
          billItemTemplate {
            id
            name
            description
            default_amount
          }
        }
        billable {
          ... on Classroom {
            id
            name
            abbreviation
          }
          ... on ClassroomSection {
            id
            classroom_id
            section_id
            classroom {
              id
              name
              abbreviation
            }
            section {
              id
              name
            }
          }
          ... on ProgrammeType {
            id
            prog_code
            program_name
            degree
          }
          ... on FormSubmission {
            id
            tracking_code
            applicant_name
          }
          ... on TranscriptRequest {
            id
            request_applicant_name: applicant_name
            request_tracking_code: tracking_code
          }
        }
      }
      openProgrammeModules @include(if: $include_tertiary) { id name registration_fee sequence academic_calendar_id min_units_per_registration max_units_per_registration started_at ended_at is_open }
        studentActiveCurriculum @include(if: $include_tertiary) {
          programme_module_id
          moduleOfferings { id }
        id
        course_id
        programme_id
        status
        semester
        course_registration_level
        school_id
        is_active
        course {
          id
          course_code
          course_title
          unit
          course_instance_id
          course_instance_year
          is_active
        }
      }
      studentOutstandingCourses @include(if: $include_tertiary) {
        compulsory {
          programme_module_ids
          course_id
          course { id course_code course_title unit }
          curriculum_level
          status
          category
          is_locked
          unit
        }
        elective {
          programme_module_ids
          course_id
          course { id course_code course_title unit }
          curriculum_level
          status
          category
          is_locked
          unit
        }
        compulsory_total_units
        elective_total_units
        passed_course_ids
        compulsory_mode
        enforce_prerequisites
      }
      studentRegistrationForCurrentSemester @include(if: $include_tertiary) {
        id
        student_id
        academic_session
        semester
        registration_level
        programme_id
        programme {
          id
          program_name
        }
        coursesRegistered {
          programme_module_id
          module_name
          module_sequence
          id
          course_id
          course_status
          course {
            id
            course_code
            course_title
            unit
          }
          scores {
            id
            score
          }
        }
      }
    }
  }
}
`;

// The notices this staff account switched off ("Don't show this again").
// Kept out of getCurrentUser so a backend without the field can only hide a
// notice, never fail the query everyone signs in with.
const GET_MY_DISMISSED_NOTICES = gql`
  query getMyDismissedNotices {
    me {
      ... on User {
        id
        dismissed_notices
      }
    }
  }
`;

// Query to fetch all session structures

const QUERY_ALL_SESSION_STRUCTURES = () => useQuery(gql`
    query GetAllSessionStructures {
      allSessionStructures {
        id
        school_id
        name
        description
        is_default
        created_at
        updated_at
        termsStructure {
          id
          name
        }
        sessions {
          id
          name
        }
      }
    }
  `);

// Query to fetch all sessions
const QUERY_ALL_SESSIONS = () => useQuery(gql`
    query GetSchoolSessions {
      schoolSessions {
        id
        name
        started_at
        ended_at
        created_at
        updated_at
      }
    }
  `);

const GET_SCHOOL_QUERY = gql`
  query GetSchool {
    getSchool {
      id
      name
      ticket_auto_response
      address
      mobile
      website
      email
      logo
      affiliate_logo
      affiliate_name
      school_type
      attendance_type
      settings

      sections {
        id
        name
      }
      academicCalendars {
        id
            is_active
        school_id
        session_id
        term_structure_id
        started_at
        ended_at
        is_active
        total_school_days
        next_calender_starts_at
        session {
          id
          school_id
          session_structure_id
          name
          started_at
          ended_at
          is_active
        }
        termStructure {
          id
          session_structure_id
          name
          sequence
        }
      }
      classrooms {
        id
        name
        abbreviation
        classroomSections {
          id
          name
          section {
            id
            name
          }
        }
      }
      classroomSections {
        id
        name
        section_id
        classroom {
          id
          name
        }
        section {
          id
          name
        }
      }
      billItems {
        id
        name
        school_id
      }
      paymentProviders {
        id
        name
        is_active
        config {
          ... on MonnifyConfig {
            apiKey
            secretKey
            contractCode
          }
          ... on PaystackConfig {
            publicKey
            secretKey
          }
          ... on RemitaConfig {
            apiKey
            merchantId
            serviceTypeId
          }
        }
      }
      is_parent
      is_child
      is_standalone
      parent_features_enabled
    }
}`;

// Query to fetch school
const QUERY_SCHOOL = () => useQuery(gql`
    query GetSchool {
      getSchool {
        id
        name
        ticket_auto_response
        address
        mobile
        email
        website
        school_type
        attendance_type
        classrooms {
          id
          name
          sequence
        }
        classroomSections {
        id
        name
        section_id
        }
        academicCalendars {
          id
          is_active
          session {
            id
            name
          }
          total_school_days
          termStructure {
            id
            name
          }
        }
      }
    }
  `);

const GET_TERM_STRUCTURES = gql`
  query GetTermStructures($session_structure_id: ID!) {
    termStructuresForStructure(session_structure_id: $session_structure_id) {
      id
      name
      sequence
      session_structure_id
      created_at
      updated_at
    }
  }
`;

const GET_BILLS = gql`
  query GetBills($filters: BillsFilterInput) {
    bills(filters: $filters) {
      id
      amount
      net_amount
      due_date
      status
      billable_type
      billable_id
      academic_calendar_id
      session_id
      school_id
      balance
      amount_paid
      created_at
      updated_at
      billable {
        ... on Student {
          id
          first_name
          surname
          other_name
          matric_no
        }
        ... on Classroom {
          id
          name
          abbreviation
        }
        ... on ClassroomSection {
          id
        classroom_id
        section_id
          classroom {
            name
            abbreviation
          }
          section {
            id
            name
          }
        }
        ... on FormSubmission {
          id
          tracking_code
          applicant_name
        }
        ... on TranscriptRequest {
          id
          request_applicant_name: applicant_name
          request_tracking_code: tracking_code
        }
      }
      billDetails {
        id
        amount
        bill_item_template_id
        billItemTemplate {
          id
          name
          default_amount
          description
        }
      }
      session {
        id
        name
      }
      academicCalendar {
        id
        termStructure {
          id
          name
        }
      }
      paymentStatusDetails {
        total_amount
        amount_paid
        balance
        payment_status
      }
    }
  }
`;

// Query to fetch bill items
const GET_BILL_ITEMS_LIST = gql`
  query GetBillItemTemplatesList {
    billItemTemplates {
      id
      name
      description
      default_amount
      school_id
      created_at
      updated_at
    }
  }
`;

// Query to fetch all sessions
const GET_SESSIONS = gql`
  query GetSchoolSessions {
    schoolSessions {
      id
      name
      started_at
      ended_at
      sessionStructure {
        id
        name
      }
    }
  }
`;

// Query to fetch students
const GET_STUDENTS = gql`
  query GetStudents($page: Int, $first: Int, $filter: StudentFilter, $include_tertiary: Boolean = false) {
    students(page: $page, first: $first, filter: $filter) {
      data {
        ...StudentDetails
        openProgrammeModules @include(if: $include_tertiary) { id name registration_fee sequence academic_calendar_id min_units_per_registration max_units_per_registration started_at ended_at is_open }
        studentActiveCurriculum @include(if: $include_tertiary) {
          programme_module_id
          moduleOfferings { id }
          id
          programme {
            id
            program_name
          }
        }
        studentAcademicCalendar {
          id
          initial_absent_days
          initial_late_days
          initial_present_days
          min_units_per_registration
          max_units_per_registration
           entry_level
           min_graduation_units
          status
          programme @include(if: $include_tertiary) {
             delivery_mode
             min_units_per_registration
             max_units_per_registration
             progression_type
            id
            program_name
          }
          academicCalendar {
            id
            termStructure {
              id
              name
            }
            session {
              id
              name
            }
          }
        }
      }
      paginatorInfo {
       ...PaginatorParts
      }
    }
  }
  ${STUDENT_FRAGMENT}
  ${PAGINATOR_FRAGMENT}
`;

const SEARCH_STUDENTS = gql`
  query SearchStudents($filter: StudentFilter, $first: Int = 10, $page: Int = 1) {
    students(filter: $filter, first: $first, page: $page) {
      data {
        id
        first_name
        surname
        other_name
        matric_no
        gender
        photo
      }
      paginatorInfo {
        ...PaginatorParts
      }
    }
  }
  ${PAGINATOR_FRAGMENT}
`;

const GET_STUDENTS_WITH_BILLS = gql`
  query GetStudentsWithBills($page: Int, $first: Int, $filter: StudentFilter) {
    students(page: $page, first: $first, filter: $filter) {
      data {
        ...StudentDetails
        ...StudentBillsDetails
      }
      paginatorInfo {
       ...PaginatorParts
      }
    }
  }
  ${STUDENT_FRAGMENT}
  ${PAGINATOR_FRAGMENT}
  ${STUDENT_BILLS_FRAGMENT}
`;

const GET_ACTIVE_ACADEMIC_CALENDAR = gql`
  query GetActiveAcademicCalendar {
    activeAcademicCalendar {
      id
      school_id
      session_id
      term_structure_id
      started_at
      ended_at
      is_active
      total_school_days
      next_calender_starts_at
      created_at
      updated_at
      session {
        id
        name
      }
      termStructure {
        id
        name
      }
    }
  }
`;

const GET_STAFF = gql`
  query GetStaff($filter: StaffFilter, $first: Int = 10, $page: Int) {
    getStaff(filter: $filter, first: $first, page: $page) {
      paginatorInfo {
        currentPage
        lastPage
        perPage
        total
      }
      data {
        id
        name
        status
        email
        department_id
        department {
          id
          department_name
        }
        createdAt
        updatedAt
        subjects {
          subject {
            id
            name
          }
          classroomSections {
            id
            name
            classroom {
              id
              name
            }
          }
        }
        lecturer_courses_in_semester {
           id
           course_id
           approved_by_id
           is_approved
           approved_at
           denied_by_id
           denied_at
           course {
            id
            course_code
            course_title
            unit
            course_instance_id
            course_instance_year
           }

        
        
        }

        roles {
          id
          grant_type
          is_primary
          category
          status
          is_active
          starts_at
          ends_at
          scope_description
          permission {
            id
            action
            resource
            key
            description
          }
          permissionGroup {
            id
            name
            description
          }
          scopes {
            id
            scope_id
            scope_type
            scope_value
          }
        }


      }
    }
  }
`;

export const GET_STAFF_BY_ID = gql`
  query GetStaff($id: ID!) {
    staff(id: $id) {
      id
      name
      email
      status
      department_id
      department {
        id
        department_name
        faculty_id
        faculty {
          id
          faculty_name
        }
      }
      createdAt
      updatedAt
      subjects {
      
        subject {
          id
          name
        }
        classroomSections {
          id
          name
          classroom {
            id
            name
          }
        }
      }
      lecturer_courses_in_semester {
        id
        
        course_id
        academic_calendar_id
        school_id
        is_approved
        approved_by_id
        approved_at
        denied_by_id
        denied_at
        created_at
        updated_at
        user {
          id
          name
          email
        }
        course {
          id
          course_code
          course_instance_id
          course_instance_year
          course_title
          unit
        }
      }

      roles {
        id
        grant_type
        is_primary
        category
        status
        is_active
        starts_at
        ends_at
        scope_description
        permission {
          id
          action
          resource
          key
          description
        }
        permissionGroup {
          id
          name
          description
        }
        scopes {
          id
          scope_id
          scope_type
          scope_value
        }
      }
    }
  }
`;

const MY_SCHOOLS = gql`
  query mySchools($search: String, $first: Int = 20, $page: Int = 1) {
    mySchools(search: $search, first: $first, page: $page) {
      data {
        id
        name
        slug
        frontend_url
        school_type
        student_portal_mode
        logo
        is_parent
        is_child
        parent_features_enabled
      }
      paginatorInfo {
        ...PaginatorParts
        hasMorePages
      }
    }
  }
  ${PAGINATOR_FRAGMENT}
`;

export {
  QUERY_STUDENT_BY_ID,
  QUERY_STUDENT_SESSION,
  QUERY_ACTIVE_SETTING,
  QUERY_STUDENT_SESSIONS,
  QUERY_SETTING,
  QUERY_STUDENTS,
  GET_CURRENT_USER,
  GET_MY_DISMISSED_NOTICES,
  QUERY_ALL_SESSIONS,
  QUERY_ALL_SESSION_STRUCTURES,
  QUERY_SCHOOL,
  GET_TERM_STRUCTURES,
  GET_BILLS,
  GET_BILL_ITEMS_LIST,
  GET_ACTIVE_ACADEMIC_CALENDAR,
  GET_SESSIONS,
  GET_STUDENTS,
  SEARCH_STUDENTS,
  GET_SCHOOL_QUERY,
  GET_STUDENTS_WITH_BILLS,
  GET_STAFF,
  MY_SCHOOLS,
};

export * from './payment';

export const GET_INVITED_TEACHERS = gql`
  query GetInvitedTeachers($first: Int = 10, $page: Int) {
    pagedSchoolInvitations(first: $first, page: $page) {
      paginatorInfo {
        currentPage
        lastPage
        perPage
        total
      }
      data {
        id
        email
        status
        created_at
        permissionGroup {
          id
          name
        }
      }
    }
  }
`;

export const GET_DEBTORS = gql`
  query GetDebtors {
    debtors {
      student {
        id
        first_name
        surname
        currentClassroomSection {
          name
        }
      }
      outstanding_balance
    }
  }
`;

export const GET_FINANCIAL_TRANSACTIONS = gql`
  query GetFinancialTransactions($student_id: ID, $status: PaymentStatusEnum) {
    payments(student_id: $student_id, status: $status) {
      id
      payment_date
      student {
        id
        first_name
        surname
      }
      amount
      payment_method
      status
      transaction_reference
    }
  }
`;

export const GET_SUBJECT_ENROLLMENTS = gql`
  query GetSubjectEnrollments($subjectId: ID!, $first: Int!, $page: Int, $academicCalendarId: ID, $classroomId: ID) {
    getSubjectEnrollments(subjectId: $subjectId, first: $first, page: $page, academicCalendarId: $academicCalendarId, classroomId: $classroomId) {
      data {
        id
        student {
          id
          first_name
          surname
          matric_no
        }
        classroomSection {
          id
          name
          classroom {
            id
            name
          }
          section {
            id
            name
          }
        }
      }
      paginatorInfo {
        ...PaginatorParts
      }
    }
  }
  ${PAGINATOR_FRAGMENT}
`;

export const GET_STUDENT_SUBJECTS = gql`
  query GetStudentSubjects($student_id: ID!, $academic_calendar_id: ID) {
    studentSubjects(student_id: $student_id, academic_calendar_id: $academic_calendar_id) {
      id
      name
    }
  }
`;

export const GET_STUDENT_ATTENDANCE = gql`
  query GetStudentAttendance($student_id: ID!, $academic_calendar_id: ID, $start_date: Date, $end_date: Date) {
    studentAttendance(student_id: $student_id, academic_calendar_id: $academic_calendar_id, start_date: $start_date, end_date: $end_date) {
      records {
        date
        status
        remark
        academicCalendar {
          session {
            name
          }
          termStructure {
            name
          }
        }
      }
      summary {
        present
        absent
        late
        excused
        total
      }
    }
  }
`;

export const GET_CLASSROOM_STUDENT_ATTENDANCE = gql`
    query GetClassroomStudentAttendance($classroom_section_id: ID!, $academic_calendar_id: ID, $start_date: Date, $end_date: Date, $page: Int, $first: Int) {
        classroomStudentAttendance(classroom_section_id: $classroom_section_id, academic_calendar_id: $academic_calendar_id, start_date: $start_date, end_date: $end_date, page: $page, first: $first) {
            data {
                student {
                    id
                    first_name
                    surname
                    matric_no
                }
                summary {
                    present
                    absent
                    late
                    excused
                    total
                }
            }
            paginatorInfo {
                currentPage
                lastPage
                perPage
                total
            }
        }
    }
`;
export const GET_STUDENT_RESULT = gql`
  query GetStudentResult($student_id: ID!, $academic_calendar_id: ID!) {
    studentResult(student_id: $student_id, academic_calendar_id: $academic_calendar_id) {
      status
      result_mode
      headers {
        key
        label
        type
        is_visible
      }
      data
      student {
        id
        first_name
        surname
        other_name
        matric_no
        gender
      }
      classroom {
        id
        name
      }
      academic_calendar {
        id
        session {
          id
          name
        }
        termStructure {
          id
          name
        }
      }
      template {
        id
        style_code
        properties
        grade_group {
          gradeBands {
            grade
            minimum
            maximum
            name
          }
        }
        columns {
            label
            key
            type
            calculation_arguments
        }
        feedbacks {
            id
            label
            code
            category
            input_type
            rating_max
            rating_scale_labels
        }
      }
      result_feedbacks {
          id
          result_template_feedback {
              id
          }
          comment
          rating
          rating_label
          author {
              name
          }
          updated_at
      }
      summary
    }
  }
`;

export const GET_AVAILABLE_ROLES = gql`
  query getAvailableRoles {
    getAvailableRoles {
      id
      name
      description
      permissions {
        id
        key
        action
        resource
      }
    }
  }
`;
export const GET_STAFF_ROLES = gql`
  query GetStaffRoles($filter: RoleGrantFilterInput, $first: Int = 20, $page: Int) {
    getStaffRoles(filter: $filter, first: $first, page: $page) {
      paginatorInfo {
        currentPage
        lastPage
        perPage
        total
      }
      data {
        id
        grant_type
        is_primary
        category
        status
        is_active
        starts_at
        ends_at
        scope_description
        permissionGroup {
          id
          name
        }
        permission {
          id
          action
          resource
          key
        }
        scopes {
          id
          scope_type
          scope_id
          scope_value
        }
      }
    }
  }
`;

export const GET_PERMISSION_DEFINITIONS = gql`
  query GetPermissionDefinitions {
    permissionDefinitions {
      key
      name
      description
      audience
      actions
      action_details {
        action
        description
      }
      available_scopes
    }
  }
`;

export const DOWNLOAD_STUDENT_RESULT = gql`
  query DownloadStudentResult($student_id: ID!, $academic_calendar_id: ID!) {
    downloadStudentResult(student_id: $student_id, academic_calendar_id: $academic_calendar_id) {
      id
      type
      status
      progress
      file_name
      file_url
      error_message
    }
  }
`;

export const GET_STUDENT_ALL_SEMESTER_RESULTS = gql`
  query GetStudentAllSemesterResults($student_id: ID!, $programme_id: ID) {
    studentAllSemesterResults(student_id: $student_id, programme_id: $programme_id) {
      student {
        id
        first_name
        surname
        other_name
        matric_no
        gender
      }
      semesters {
        academic_calendar {
          id
          session { id name }
          termStructure { id name }
        }
        programme {
          id
          program_name
          degree
        }
        template {
          id
          style_code
          properties
          grade_group {
            gradeBands {
              grade
              minimum
              maximum
              point
              name
            }
          }
          columns {
            key
            label
            type
            is_visible
            calculation_function
            calculation_arguments
          }
        }
        headers { key label type is_visible }
        data
        summary
        gpa
        cumulative_cgpa
        status
      }
    }
  }
`;

export const GET_STUDENT_REGISTERED_CALENDARS = gql`
  query GetStudentRegisteredCalendars($student_id: ID!, $programme_id: ID) {
    studentRegisteredCalendars(student_id: $student_id, programme_id: $programme_id) {
      id
      is_active
      started_at
      session { id name }
      termStructure { id name sequence }
    }
  }
`;

export const GET_STUDENT_ENROLLED_PROGRAMMES = gql`
  query GetStudentEnrolledProgrammes($student_id: ID!) {
    studentEnrolledProgrammes(student_id: $student_id) {
      id
      program_name
      degree
      is_active_programme
    }
  }
`;

export const DOWNLOAD_STUDENT_ALL_SEMESTERS_RESULT = gql`
  query DownloadStudentAllSemestersResult($student_id: ID!, $programme_id: ID) {
    downloadStudentAllSemestersResult(student_id: $student_id, programme_id: $programme_id) {
      id
      type
      status
      progress
      file_name
      file_url
      error_message
    }
  }
`;

// ... existing code ...

export const QUERY_DASHBOARD_OVERVIEW = (options: { autoFetch?: boolean } = { autoFetch: true }) => {
  const queryResult = useLazyQuery<{ dashboardOverview: DashboardStats }, Record<string, never>>(
    gql`
      query GetDashboardOverview {
        dashboardOverview {
          totalStudents
          maleStudentsCount
          femaleStudentsCount
          totalStaff
          newStaffCount

          attendanceToday {
            present
            absent
            late
            percentage
            total
          }

          currentWeek
          weekEndsInDays
          calendarStatus

          ticketing {
              all
              open
              resolved
              highPriorityOpen
          }

          auditLogs(limit: 5) {
            id
            event
            event_label
            created_at
            user {
              name
              id
            }
          }

          activeCbtTests {
            id
            title
            start_time
            end_time
          }
          upcomingCbtTests {
            id
            title
            start_time
          }

          attendanceTrend {
            date
            present
            absent
            late
          }
        }
      }
    `,
    {},
  );
  if (options.autoFetch) (queryResult as unknown as { load?: () => void }).load?.();
  return queryResult;
};

export * from './Timetable';
export * from './programmeResultTemplate';

export const GET_PDF_RESULT_CONFIG = gql`
  query GetPdfResultConfig {
    pdfResultConfig {
      preset
      logo_position
      logo_height
      show_school_name
      show_school_address
      show_report_title
      report_title
      subject_column_width
      font_size
      header_font_size
      row_padding
      show_borders
      striped_rows
      alternate_row_color
      show_student_info
      student_info_fields
      show_summary
      show_grading_key
      show_attendance
      show_remarks
      show_footer
      footer_text
      page_size
      page_orientation
      page_margin
      show_signature_lines
      signature_labels
      show_watermark
      watermark_text
      show_next_term_date
    }
  }
`;

export const GET_PDF_RESULT_PRESETS = gql`
  query GetPdfResultPresets {
    pdfResultPresets {
      key
      name
      description
      config {
        preset
        logo_position
        logo_height
        show_school_name
        show_school_address
        show_report_title
        report_title
        subject_column_width
        font_size
        header_font_size
        row_padding
        show_borders
        striped_rows
        alternate_row_color
        show_student_info
        show_summary
        show_grading_key
        show_attendance
        show_remarks
        show_footer
        footer_text
        page_size
        page_orientation
        page_margin
        show_signature_lines
        signature_labels
        show_watermark
        watermark_text
        show_next_term_date
      }
    }
  }
`;

export const GET_MY_PENDING_SCORE_ENTRIES = gql`
  query GetMyPendingScoreEntries {
    myPendingScoreEntries {
      name
      classroomSection
      totalStudents
      studentsWithScores
      entityId
      classroomSectionId
    }
  }
`;
